/* eslint-disable react/jsx-no-bind */
/* eslint-disable react/prop-types */
import { CircularProgress, Typography } from '@material-ui/core';
import axios from 'axios';
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Cell, Legend, Pie, PieChart, Tooltip,
} from 'recharts';
import CountyContext from '../../contexts/CountyContext';
import StoryContext from '../../contexts/StoryContext';
import colors from '../../modules/colors';
import { rdsTabulate } from '../../modules/rds';
import states, { stateFIPS } from '../../modules/states';
import { toFormattedNumber, toPercent } from '../../modules/text';
import CustomTooltip from '../CustomTooltip';
import ResponsiveContainer from '../ResponsiveContainer';

function AccessByQualityRatingSubsidy({ height = 300 }) {
  const { stateCode } = useParams();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const { countyCode, county } = useContext(CountyContext);
  const {
    primaryColor, darkColor, lightColor,
  } = useContext(StoryContext);

  useEffect(() => {
    // const server = new RdsServer(process.env.REACT_APP_RDS_SERVER);
    setError(null);
    async function getData() {
      // Instantiate a catalog that exists on the server
      // const catalog = new RdsCatalog(server, 'ccdc');
      // Instantiate a data product that exists on the catalog
      // const dataProduct = new RdsDataProduct(catalog, 'provider');
      let whereClause = `state_fips=${stateFIPS[stateCode.toUpperCase()]} AND license_type=LIC AND is_subsidized=1`;

      if (countyCode) {
        whereClause += ` AND county_fips=${countyCode}`;
      }

      rdsTabulate('ccdc', 'provider', {
        dims: 'qris_code',
        measure: 'count:COUNT(*)',
        where: whereClause,
        format: 'amcharts',
        inject: false,
      })
        .then((result) => {
          const fixedData = result.parsedBody.dataProvider.map((data) => {
            const item = { ...data };
            if (!item.qris_code) {
              // item.qris_code = `${stateCode.toUpperCase()}0`;
              item.qris_code = 'Blank';
            }
            return item;
          });
          const d = _.orderBy(fixedData, ['qris_code'], ['asc']);
          const ratingIds = d.map((v) => (v.qris_code));
          axios.get(`${process.env.REACT_APP_RDS_SERVER}/api/catalog/ccdc/provider/classification/qris_code/codes?ids=${ratingIds.join(',')}`)
            .then((idsResponse) => {
              const codes = idsResponse.data;
              const total = d.reduce((a, v) => (a + Number.parseInt(v.count, 10)), 0);
              /**
               * People-friendly QRIS code naming, with special handling for blank, NR, and null entries.
               * @param {string} code
               * @returns {string} QRIS code name
               */
              const getQrisCode = (code) => {
                if (code === null) { return 'Null'; }
                if (code === 'Blank') { return 'Blank'; }
                if (code === `${stateCode.toUpperCase()}0`) { return 'NR'; }
                return _.find(codes, { codeValue: code }).name.replace(`${stateCode.toUpperCase()}: `, '');
              };
              const payload = d.map((v) => Object.assign(v, {
                percent: ((v.count / total) * 100),
                qris_code: getQrisCode(v.qris_code),
              }));
              setData(payload);
            }).catch((getError) => {
              setError(getError);
            })
            .finally(() => {
              setReady(true);
            });
        })
        .catch((rdsError) => {
          setError(rdsError);
        })
        .finally(() => {
          setReady(true);
        });
    }
    if (stateCode && states[stateCode.toUpperCase()]) {
      // fetch actual average price data
      getData();
    }
  }, [stateCode, countyCode]);

  let title = 'Program Quality Rating';
  if (county) {
    title += ` - ${county} County`;
  }

  const tooltipFormatter = function tooltipFormatter(payload) {
    let result = `Of the providers who accept subsidies, ${toFormattedNumber(payload.payload.percent)}%`;
    if (payload.name === 'Blank') {
      result += ' do not report quality rating.';
    } else {
      result += ` have quality rating ${payload.name}.`;
    }
    return result;
  };

  const colorsArray = [darkColor, primaryColor, lightColor, colors.darkTaupe, colors.taupe, colors.grey].reverse();

  if (!ready) {
    return <CircularProgress />;
  }

  return (
    <>
      {error && <Typography variant="body1" color="error">{error.message}</Typography>}
      <Typography variant="body2" align="center"><strong>{title}</strong></Typography>
      <ResponsiveContainer height={height}>
        <PieChart>
          <Pie
            data={data}
            dataKey="count"
            nameKey="qris_code"
            startAngle={0}
            outerRadius={height / 3.2}
            label={({
              cx,
              cy,
              midAngle,
              innerRadius,
              outerRadius,
              // value,
              percent,
            }) => {
              const RADIAN = Math.PI / 180;
              // eslint-disable-next-line
              const radius = 25 + innerRadius + (outerRadius - innerRadius);
              // eslint-disable-next-line
              const x = cx + radius * Math.cos(-midAngle * RADIAN);
              // eslint-disable-next-line
              const y = cy + radius * Math.sin(-midAngle * RADIAN);

              return (
                <text
                  x={x}
                  y={y}
                  fill="#000"
                  textAnchor={x > cx ? 'start' : 'end'}
                  dominantBaseline="central"
                >
                  {toPercent(percent / 100)}
                </text>
              );
            }}
            labelLine
            fill={primaryColor}
          >
            {data.map((entry, index) => (
              <Cell key={entry} fill={colorsArray[index]} />
            ))}
          </Pie>
          <Tooltip
            cursor={false}
            content={(
              <CustomTooltip
                formatterFunction={tooltipFormatter}
              />
            )}
          />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </>
  );
}

export default AccessByQualityRatingSubsidy;
